// The Orders section's presentation maps (KOVA-797): what a model's glyph and status tone look like. Which
// glyph a timeline row carries and which tone a badge has is the model's decision (`TimelineItem.glyph`,
// `StatusBadge.tone`); this file only knows how each one is drawn.
//
// Glyphs are SVG markup (`?raw`) the views inline with `v-html`, so they paint in `currentColor`: the DS
// `Timeline` paints its node glyph white on a reached step and grey on an upcoming one, the tiles and the
// renewal card tint theirs. The back arrow is the DS `Icon` (`ds_arrow_left`, in `OrderDetails`).
import type { BadgeTone } from '@simple-life-app/growth-ui-kova'
import type { TimelineItem } from '@/orders/detailModel'
import type { StatusTone } from '@/orders/status'
import arrowRight from '@/assets/icons/arrow-right.svg?raw'
import checkmark from '@/assets/icons/checkmark.svg?raw'
import warningTriangle from '@/assets/icons/warning-triangle.svg?raw'
import docApproved from '@/assets/orders/doc-approved.svg?raw'
import doc from '@/assets/orders/doc.svg?raw'
import home from '@/assets/orders/home.svg?raw'
import returnGlyph from '@/assets/orders/return.svg?raw'
import truck from '@/assets/orders/truck.svg?raw'
import xCircle from '@/assets/orders/x-circle.svg?raw'

/** The DS `Badge` tone of a status pill colour (Figma "Order statuses": the DS ships the same pairs). */
export const BADGE_TONE: Record<StatusTone, BadgeTone> = {
	green: 'success',
	blue: 'info',
	orange: 'warning',
	red: 'error',
	grey: 'neutral',
}

/**
 * The shared `src/assets/icons/` art bakes its Figma colour into `fill` (the cancel flow draws it as an `<img>`);
 * inlined here, it takes the colour of the place it sits in instead. Its Figma layer ids go too: inlined once per
 * card or timeline row, they would repeat across the page.
 */
function inCurrentColor(svg: string): string {
	return svg.replace(/fill="#[0-9a-f]{3,8}"/gi, 'fill="currentColor"').replace(/\s+id="[^"]*"/g, '')
}

/** Figma `icon-arrow-right`: the order card's tile (the cancel flow's option rows draw the same one). */
export const ARROW_RIGHT_SVG = inCurrentColor(arrowRight)
/** Figma `icon-warning`: the renewal card at Warning / Error and the next shipment's day. */
export const WARNING_SVG = inCurrentColor(warningTriangle)
/** Figma's document: the renewal card at Info, a timeline row awaiting review. */
export const DOC_SVG = doc
export const TRUCK_SVG = truck

/** A timeline glyph to draw: its markup, and whether it is drawn upside down (Figma's `icon-checkmark` is). */
export interface OrderGlyph {
	/** A stable, PII-free name — what a test can read off the markup (`data-glyph`). */
	name: string
	svg: string
	flipped: boolean
}

const glyph = (name: string, svg: string, flipped = false): OrderGlyph => ({ name, svg, flipped })

const CHECK = glyph('check', inCurrentColor(checkmark), true)
const DOC = glyph('doc', DOC_SVG)
const DOC_APPROVED = glyph('doc-approved', docApproved)
const TRUCK = glyph('truck', TRUCK_SVG)
// The Figma house with its narrow door; the DS `ds_home` is another drawing (a wide, low doorway).
const HOME = glyph('home', home)
const X_CIRCLE = glyph('x-circle', xCircle)
const RETURN = glyph('return', returnGlyph)

/** The glyph in a timeline row's node, or `null` for a row that has none (the small substatus rows). */
export function timelineGlyph(item: Pick<TimelineItem, 'glyph'>): OrderGlyph | null {
	switch (item.glyph) {
		case 'check':
			return CHECK
		case 'doc':
			return DOC
		case 'docApproved':
			return DOC_APPROVED
		case 'truck':
			return TRUCK
		case 'home':
			return HOME
		case 'failed':
			return X_CIRCLE
		case 'returned':
			return RETURN
		default:
			return null
	}
}
